// Interface for handling virtual and live agent interactions
// All Firebase operations are now handled by the backend via REST API
import { getIdToken } from '../contexts/AuthContext';
import { buildApiUrl, getAuthHeaders } from '../config/api';
import { SkiLiftFeatureExtractor } from './TelemetryFeatures';

// Error callback - can be set by the application to receive errors
let errorCallback = null;

/**
 * Set the error callback function to receive errors from the interface
 * @param {Function} callback - Function that receives (message, details, source)
 */
export const setErrorCallback = (callback) => {
  errorCallback = callback;
};

/**
 * Internal helper to report errors
 * @private
 */
const _reportError = (message, details, source = 'Interface') => {
  if (errorCallback) {
    errorCallback(message, details, source);
  } else {
    console.error(`[${source}] ${message}`, details || '');
  }
};

/** @typedef {import('../models/dataModels').User} User */
/** @typedef {import('../models/dataModels').SkierDataModel} SkierDataModel */

/**
 * Creates a SkierDataModel object for backend API
 * @param {Object} telemetryData - Telemetry data containing location and stats
 * @param {Object} telemetryData.location - Location data with lat, lon, altitude, course, accuracy, speed
 * @param {Object} telemetryData.stats - Optional stats data with maxVelocitymps, totVerticalMeters, timestamp
 * @returns {SkierDataModel} Skier data model object
 */
export const createSkierDataModel = (telemetryData) => {
  const model = {
    updatedAt: new Date().toISOString()
  };

  if (telemetryData.location && 
      telemetryData.location.lat !== null && 
      telemetryData.location.lat !== void 0 &&
      telemetryData.location.lon !== null && 
      telemetryData.location.lon !== void 0) {
    model['telemetry.coordinate'] = { 
      latitude: telemetryData.location.lat, 
      longitude: telemetryData.location.lon 
    };
    model['telemetry.altitude'] = telemetryData.location.altitude ?? 0;
    model['telemetry.heading'] = telemetryData.location.course ?? 0;
    model['telemetry.horizAccuracy'] = telemetryData.location.accuracy ?? 0;
    model['telemetry.speed'] = telemetryData.location.speed ?? 0;
    model['telemetry.timestamp'] = new Date().toISOString();

    if (typeof telemetryData.location.timestamp === 'number' && Number.isFinite(telemetryData.location.timestamp)) {
      model['telemetry.simTimestamp'] = telemetryData.location.timestamp;
    }
  }

  if (telemetryData.stats) {
    model['stats.maxVelocitymps'] = telemetryData.stats.maxVelocitymps ?? 0;
    model['stats.totVerticalMeters'] = telemetryData.stats.totVerticalMeters ?? 0;
    model['stats.lastUpdate'] = telemetryData.stats.timestamp ?? 0;
  }

  return model;
};

/**
 * SimulationInterface - Manages virtual and live agent interactions
 * All Firebase operations are handled by the backend via REST API.
 * Ensures only one simulation can run at a time.
 */
class SimulationInterface {
  constructor() {
    // Track initialized state
    this.initialized = false;

    // Track active simulation to ensure only one runs at a time
    this.activeSimulationId = null;

    // Store live users for reference
    this.liveUsers = [];

    // Track whether live agent subscriptions are currently active
    this.liveAgentSubscriptionsActive = false;

    // Optional simulation context (e.g., selected ski area for feature detection)
    this.skiAreaId = null;

    // Cached GeoJSON for current ski area context
    /** @type {import('geojson').FeatureCollection | null} */
    this.lifts = null;
    /** @type {import('geojson').FeatureCollection | null} */
    this.runs = null;
    this._geojsonLoadedForSkiAreaId = null;
    this._geojsonLoadPromise = null;

    /**
     * Chair/run classification buffer.
     * @type {Array<{altitude:number, speed:number, course:number}>}
     * @private
     */
    this._chairRunBuffer = [];

    /**
     * Number of samples to consider when classifying chair vs run.
     * @type {number}
     * @private
     */
    this._chairRunWindowSize = 10;

    /**
     * Distance threshold for feature detection (meters).
     * @type {number}
     * @private
     */
    this._runAndChairliftDetectionDistanceMeters = 50;

    /**
     * Per-virtual-agent rolling feature extractors (uid -> extractor).
     * @type {Map<string, SkiLiftFeatureExtractor>}
     * @private
     */
    this._skiLiftFeatureExtractorsByUid = new Map();

    /**
     * Track last timestamp used for per-agent rolling feature extraction.
     * @type {Map<string, number>}
     * @private
     */
    this._lastFeatureTimestampByUid = new Map();
  }

  /**
   * Get or create a rolling feature extractor for a given virtual agent.
   * @private
   * @param {string} uid
   * @returns {SkiLiftFeatureExtractor}
   */
  _getOrCreateSkiLiftFeatureExtractor(uid) {
    let extractor = this._skiLiftFeatureExtractorsByUid.get(uid);
    if (!extractor) {
      extractor = new SkiLiftFeatureExtractor();
      this._skiLiftFeatureExtractorsByUid.set(uid, extractor);
    }
    return extractor;
  }

  /**
   * Run per-tick rolling feature extraction for a virtual agent.
   * @private
   */
  _processVirtualAgentTelemetryFeatures(user, skierDataModel) {
    const uid = user?.uid;
    if (!uid) return null;

    const tsCandidate =
      typeof skierDataModel?.['telemetry.simTimestamp'] === 'number'
        ? skierDataModel['telemetry.simTimestamp']
        : typeof skierDataModel?.['stats.lastUpdate'] === 'number'
          ? skierDataModel['stats.lastUpdate']
          : Date.now() / 1000;

    if (!Number.isFinite(tsCandidate)) return null;

    const prevTs = this._lastFeatureTimestampByUid.get(uid);
    const dt = typeof prevTs === 'number' ? tsCandidate - prevTs : null;

    // If time moves backward OR we jump forward too far, reset extractor state.
    if (dt !== null && (dt < 0 || dt > 5)) {
      console.log('[Interface] Timeline jump detected; resetting feature extractor', {
        uid,
        prevTs,
        tsCandidate,
        dt
      });
      this._skiLiftFeatureExtractorsByUid.set(uid, new SkiLiftFeatureExtractor());
    }

    this._lastFeatureTimestampByUid.set(uid, tsCandidate);

    const extractor = this._getOrCreateSkiLiftFeatureExtractor(uid);

    const altitude =
      typeof skierDataModel?.['telemetry.altitude'] === 'number' ? skierDataModel['telemetry.altitude'] : 0;
    const speed =
      typeof skierDataModel?.['telemetry.speed'] === 'number' ? skierDataModel['telemetry.speed'] : 0;
    const course =
      typeof skierDataModel?.['telemetry.heading'] === 'number' ? skierDataModel['telemetry.heading'] : 0;

    return extractor.process(tsCandidate, altitude, speed, course);
  }

  /**
   * Get ID token for API authentication
   * @private
   */
  async _getIdToken() {
    const token = await getIdToken();
    if (!token) {
      throw new Error('Not authenticated. Please log in.');
    }
    return token;
  }

  /**
   * Make an authenticated API request
   * @private
   */
  async _apiRequest(endpoint, method = 'GET', body = null) {
    const idToken = await this._getIdToken();
    const url = buildApiUrl(endpoint);
    
    const options = {
      method,
      headers: getAuthHeaders(idToken)
    };
    
    if (body && method !== 'GET') {
      options.body = JSON.stringify(body);
    }
    
    const response = await fetch(url, options);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `API request failed: ${response.status}`);
    }
    
    return response.json();
  }

  /**
   * Build candidate URLs for ski area GeoJSON
   * @private
   */
  _getSkiAreaGeojsonCandidateUrls(kind, skiAreaId) {
    const safeId = encodeURIComponent(String(skiAreaId || '').trim());
    if (!safeId) return [];
    return [`/api/ski-areas/${safeId}/${kind}`];
  }

  /**
   * Fetch and parse a GeoJSON FeatureCollection.
   * @private
   */
  async _fetchJson(url, label) {
    const idToken = await this._getIdToken();
    let response;
    try {
      response = await fetch(url, {
        cache: 'force-cache',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      throw new Error(`${label}: network error fetching ${url}: ${error?.message || String(error)}`);
    }

    if (!response.ok) {
      throw new Error(`${label}: failed to fetch ${url}: ${response.status} ${response.statusText}`);
    }

    try {
      return await response.json();
    } catch (error) {
      throw new Error(`${label}: invalid JSON from ${url}: ${error?.message || String(error)}`);
    }
  }

  /**
   * Validate GeoJSON FeatureCollection
   * @private
   */
  _assertFeatureCollection(geo, label) {
    if (!geo || typeof geo !== 'object') {
      throw new Error(`${label}: GeoJSON is empty or not an object`);
    }
    if (geo.type !== 'FeatureCollection') {
      throw new Error(`${label}: expected type "FeatureCollection", got "${geo.type}"`);
    }
    if (!Array.isArray(geo.features)) {
      throw new Error(`${label}: expected "features" to be an array`);
    }
    return geo;
  }

  /**
   * Load ski area GeoJSON
   * @private
   */
  async _loadSkiAreaGeojson(kind, skiAreaId) {
    const urls = this._getSkiAreaGeojsonCandidateUrls(kind, skiAreaId);
    if (urls.length === 0) {
      throw new Error(`[Interface] No skiAreaId provided for ${kind} GeoJSON`);
    }

    const errors = [];
    for (const url of urls) {
      try {
        const json = await this._fetchJson(url, `[Interface] ${kind}_${skiAreaId}`);
        return this._assertFeatureCollection(json, `[Interface] ${kind}_${skiAreaId}`);
      } catch (e) {
        errors.push(e instanceof Error ? e : new Error(String(e)));
      }
    }

    const msg = `[Interface] Unable to load ${kind}_${skiAreaId}.geojson (tried ${urls.length} location(s))`;
    const details = errors.map((e) => e.message).join(' | ');
    throw new Error(`${msg}: ${details}`);
  }

  /**
   * Ensure ski area GeoJSON is loaded
   * @private
   */
  async _ensureSkiAreaGeoLoaded() {
    if (!this.skiAreaId) return;

    if (this._geojsonLoadedForSkiAreaId === this.skiAreaId && this.lifts && this.runs) {
      return;
    }

    if (this._geojsonLoadedForSkiAreaId !== this.skiAreaId) {
      this.lifts = null;
      this.runs = null;
      this._geojsonLoadPromise = null;
    }

    if (!this._geojsonLoadPromise) {
      const skiAreaId = this.skiAreaId;
      this._geojsonLoadPromise = (async () => {
        const [lifts, runs] = await Promise.all([
          this._loadSkiAreaGeojson('lifts', skiAreaId),
          this._loadSkiAreaGeojson('runs', skiAreaId),
        ]);
        if (this.skiAreaId === skiAreaId) {
          this.lifts = lifts;
          this.runs = runs;
          this._geojsonLoadedForSkiAreaId = skiAreaId;
        }
      })();
    }

    try {
      await this._geojsonLoadPromise;
    } catch (error) {
      _reportError(
        'Failed to load ski area GeoJSON',
        error?.message || String(error),
        'Interface'
      );
    }
  }

  /**
   * Detect feature (lift or run) based on telemetry
   */
  detectFeature(telem, features = null) {
    if (!this.skiAreaId) {
      return null;
    }

    const maybeDetection = this.classifyChairOrRun(telem, features);
    if (!maybeDetection || maybeDetection === 'unknown') {
      return null;
    }

    const lat = telem?.coordinate2D?.lat ?? null;
    const lon = telem?.coordinate2D?.lon ?? null;
    if (lat === null || lon === null) {
      return null;
    }

    const coord = { lat, lon };

    if (maybeDetection === 'chair') {
      const maybeFeature = this.identifyChairLift(coord);
      if (maybeFeature == null) {
        return this.identifyRun(coord);
      }
      return maybeFeature;
    }

    if (maybeDetection === 'run') {
      const maybeFeature = this.identifyRun(coord);
      if (maybeFeature == null) {
        return this.identifyChairLift(coord);
      }
      return maybeFeature;
    }

    return null;
  }

  /**
   * Classify chair or run from telemetry/features
   */
  classifyChairOrRun(telem, features = null) {
    if (!features || typeof features !== 'object') {
      return 'unknown';
    }
    return this._classifyChairOrRunFromFeatures(features);
  }

  /**
   * Classify from rolling-window features
   * @private
   */
  _classifyChairOrRunFromFeatures(f) {
    // Lifts: ascending + constant speed + straight line
    if (
      f.verticalVelocityMean > 0.3 &&
      f.speedStd < 0.8 &&
      f.turnRateStd < 8.0
    ) {
      return 'chair';
    }

    // Runs: descending + variable speed OR turning
    if (
      f.verticalVelocityMean < -0.5 &&
      (f.speedStd > 1.0 || f.turnRateStd > 10.0)
    ) {
      return 'run';
    }

    return 'unknown';
  }

  /**
   * Identify chairlift from coordinate
   */
  identifyChairLift(coordinate2D) {
    if (!this.lifts || !Array.isArray(this.lifts.features)) {
      return null;
    }

    for (const lift of this.lifts.features) {
      if (this._smartIsLocationOnSegment(lift, coordinate2D)) {
        const id = lift?.properties?.id;
        const name = lift?.properties?.name;
        if (!id || !name) return null;
        return {
          type: 'lift',
          id: String(id),
          name
        };
      }
    }

    return null;
  }

  /**
   * Identify run from coordinate
   */
  identifyRun(coordinate2D) {
    if (!this.runs || !Array.isArray(this.runs.features)) {
      return null;
    }

    for (const run of this.runs.features) {
      if (this._smartIsLocationOnSegment(run, coordinate2D)) {
        const id = run?.properties?.id;
        const name = run?.properties?.name;
        if (!id || !name) return null;
        const color = run?.properties?.colorName || 'unknown';
        return {
          type: 'run',
          id: String(id),
          name,
          color
        };
      }
    }

    return null;
  }

  /**
   * Check if location is on a GeoJSON segment
   * @private
   */
  _smartIsLocationOnSegment(feature, location) {
    if (!feature || !feature.geometry || !location) return false;
    const { lat, lon } = location;
    if (typeof lat !== 'number' || typeof lon !== 'number') return false;

    const thresholdMeters = this._runAndChairliftDetectionDistanceMeters;
    const geom = feature.geometry;

    if (geom.type === 'LineString') {
      return this._distancePointToLineStringMeters({ lat, lon }, geom.coordinates) <= thresholdMeters;
    }
    if (geom.type === 'MultiLineString') {
      for (const line of geom.coordinates || []) {
        if (this._distancePointToLineStringMeters({ lat, lon }, line) <= thresholdMeters) {
          return true;
        }
      }
      return false;
    }
    if (geom.type === 'Polygon') {
      return this._pointInPolygon({ lat, lon }, geom.coordinates);
    }
    if (geom.type === 'MultiPolygon') {
      for (const polygonCoords of geom.coordinates || []) {
        if (this._pointInPolygon({ lat, lon }, polygonCoords)) {
          return true;
        }
      }
      return false;
    }

    return false;
  }

  _pointInPolygon(point, polygonCoordinates) {
    if (!Array.isArray(polygonCoordinates) || polygonCoordinates.length === 0) return false;
    const outer = polygonCoordinates[0];
    if (!this._pointInRing(point, outer)) return false;
    for (let i = 1; i < polygonCoordinates.length; i++) {
      if (this._pointInRing(point, polygonCoordinates[i])) {
        return false;
      }
    }
    return true;
  }

  _pointInRing(point, ring) {
    if (!Array.isArray(ring) || ring.length < 3) return false;
    const x = point.lon;
    const y = point.lat;

    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const xi = ring[i]?.[0];
      const yi = ring[i]?.[1];
      const xj = ring[j]?.[0];
      const yj = ring[j]?.[1];
      if (
        typeof xi !== 'number' || typeof yi !== 'number' ||
        typeof xj !== 'number' || typeof yj !== 'number'
      ) {
        continue;
      }

      const intersects =
        (yi > y) !== (yj > y) &&
        x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
      if (intersects) inside = !inside;
    }
    return inside;
  }

  _distancePointToLineStringMeters(point, lineCoordinates) {
    if (!Array.isArray(lineCoordinates) || lineCoordinates.length < 2) return Infinity;
    let min = Infinity;
    for (let i = 0; i < lineCoordinates.length - 1; i++) {
      const a = lineCoordinates[i];
      const b = lineCoordinates[i + 1];
      if (!a || !b) continue;
      const d = this._perpendicularDistanceMeters(point, { lon: a[0], lat: a[1] }, { lon: b[0], lat: b[1] });
      if (d < min) min = d;
    }
    return min;
  }

  _perpendicularDistanceMeters(p, a, b) {
    const pointVector = { x: p.lon, y: p.lat };
    const startVector = { x: a.lon, y: a.lat };
    const endVector = { x: b.lon, y: b.lat };

    const lineVector = { x: endVector.x - startVector.x, y: endVector.y - startVector.y };
    const pointToStartVector = { x: pointVector.x - startVector.x, y: pointVector.y - startVector.y };

    const lineLengthSquared = lineVector.x * lineVector.x + lineVector.y * lineVector.y;
    if (lineLengthSquared === 0) {
      return this._haversineMeters(p, a);
    }

    const tRaw =
      (pointToStartVector.x * lineVector.x + pointToStartVector.y * lineVector.y) / lineLengthSquared;
    const t = Math.max(0, Math.min(1, tRaw));

    const projection = { x: startVector.x + t * lineVector.x, y: startVector.y + t * lineVector.y };
    const projectedCoordinate = { lat: projection.y, lon: projection.x };

    return this._haversineMeters(p, projectedCoordinate);
  }

  _haversineMeters(a, b) {
    const R = 6371000;
    const toRad = (deg) => (deg * Math.PI) / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLon = toRad(b.lon - a.lon);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);

    const sinDLat = Math.sin(dLat / 2);
    const sinDLon = Math.sin(dLon / 2);
    const h = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLon * sinDLon;
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
  }

  /**
   * Initialize the interface with virtual and live agents
   * All Firebase operations are now handled by the backend.
   * @param {Array<User>} virtualUsers - Array of User objects for virtual agents
   * @param {Array<User>} liveUsers - Array of User objects for live agents
   * @param {string} simulationId - Unique identifier for this simulation run
   * @param {Object} [options]
   * @param {string|null} [options.skiAreaId] - Optional ski area id
   */
  async initialize(virtualUsers, liveUsers, simulationId, options = {}) {
    // Ensure only one simulation runs at a time
    if (this.activeSimulationId !== null && this.activeSimulationId !== simulationId) {
      console.warn(`Canceling previous simulation ${this.activeSimulationId} to start new simulation ${simulationId}`);
      await this.cleanup();
    }

    if (this.initialized) {
      console.warn('SimulationInterface already initialized. Cleaning up first.');
      await this.cleanup();
    }

    // Store optional simulation context
    this.skiAreaId = options?.skiAreaId ?? null;
    
    // Reset GeoJSON cache
    this.lifts = null;
    this.runs = null;
    this._geojsonLoadedForSkiAreaId = null;
    this._geojsonLoadPromise = null;
    this._chairRunBuffer = [];
    this._skiLiftFeatureExtractorsByUid.clear();
    this._lastFeatureTimestampByUid.clear();

    // Store live users for reference
    this.liveUsers = liveUsers.filter(user => user && user.uid);

    // Initialize simulation on backend
    const virtualUserUids = virtualUsers.filter(u => u && u.uid).map(u => u.uid);
    const liveUserUids = this.liveUsers.map(u => u.uid);

    try {
      await this._apiRequest('/simulation/initialize', 'POST', {
        simulationId,
        virtualUserUids,
        liveUserUids,
        skiAreaId: this.skiAreaId
      });
    } catch (error) {
      _reportError('Failed to initialize simulation', error.message, 'Interface');
      throw error;
    }

    this.initialized = true;
    this.activeSimulationId = simulationId;
    this.liveAgentSubscriptionsActive = false;
  }

  /**
   * Called on each tick for a virtual agent. Resolves which lift or run the agent is on;
   * the Firestore write is batched separately by the page (src/services/lvcFirestore.js).
   * @returns {Promise<{detectedFeature: Object|null}>}
   */
  async onVirtualAgentTick(user, skierDataModel) {
    const result = {
      detectedFeature: null
    };

    if (this.activeSimulationId === null) {
      return result;
    }

    if (!this.initialized) {
      console.error('SimulationInterface not initialized. Call initialize() first.');
      return result;
    }

    if (!user || !user.uid) {
      console.error('Invalid user object provided to onVirtualAgentTick');
      return result;
    }

    // Telemetry rolling feature extraction
    let extractedFeatures = null;
    try {
      extractedFeatures = this._processVirtualAgentTelemetryFeatures(user, skierDataModel);
    } catch (error) {
      _reportError(
        'Error extracting telemetry features',
        error?.message || String(error),
        'Interface'
      );
    }

    // Feature detection
    await this._ensureSkiAreaGeoLoaded();

    const lat = skierDataModel?.['telemetry.coordinate']?.latitude;
    const lon = skierDataModel?.['telemetry.coordinate']?.longitude;
    if (lat !== null && lat !== void 0 && lon !== null && lon !== void 0) {
      result.detectedFeature = this.detectFeature({
        coordinate2D: { lat, lon },
        altitude: typeof skierDataModel?.['telemetry.altitude'] === 'number' ? skierDataModel['telemetry.altitude'] : 0,
        speed: typeof skierDataModel?.['telemetry.speed'] === 'number' ? skierDataModel['telemetry.speed'] : null,
        heading: typeof skierDataModel?.['telemetry.heading'] === 'number' ? skierDataModel['telemetry.heading'] : 0,
      }, extractedFeatures);
    }

    // Upload no longer happens here. The page batches every agent's telemetry into one
    // writeBatch per second (src/services/lvcFirestore.js), so a run makes one Firestore
    // round trip instead of one HTTP request per agent per second.

    return result;
  }

  async cleanup() {
    try {
      await this._apiRequest('/simulation/terminate', 'POST', {});
    } catch (error) {
      // Log but don't throw - cleanup should always succeed from client perspective
      console.error('Error terminating simulation on backend:', error);
    }

    // Reset local state
    this.liveUsers = [];
    this.liveAgentSubscriptionsActive = false;

    // Reset simulation context
    this.skiAreaId = null;
    this.lifts = null;
    this.runs = null;
    this._geojsonLoadedForSkiAreaId = null;
    this._geojsonLoadPromise = null;
    this._skiLiftFeatureExtractorsByUid.clear();
    this._lastFeatureTimestampByUid.clear();

    this.initialized = false;
    this.activeSimulationId = null;
  }
}

// Export singleton instance
const simulationInterface = new SimulationInterface();

// Export methods for backward compatibility and convenience
export const initialize = async (virtualUsers, liveUsers, simulationId, options = {}) => {
  await simulationInterface.initialize(virtualUsers, liveUsers, simulationId, options);
};

export const onVirtualAgentTick = (user, skierDataModel) => {
  return simulationInterface.onVirtualAgentTick(user, skierDataModel);
};

export const cleanup = async () => {
  await simulationInterface.cleanup();
};

// Export the class and instance for advanced usage
export { SimulationInterface };
export default simulationInterface;
